// The browser client of the selected saved App's NATIVE Data read (APP's `appData` service): what the
// Data tab shows, read for exactly one selection. Two reads, both on the SESSION-BOUND fetch the host
// captured, and neither writes:
//   GET  /api/v1/company/artifacts/:artifactId/data?versionId&revisionId&purpose  -> the readable collections
//   POST /api/v1/company/artifacts/:artifactId/data {artifactId,versionId,revisionId,purpose,collectionId,fields?,limit?,cursor?}
//        -> one page of a canonical Dataset (its next page from `nextCursor`), or {ok:false,error}
// No company, actor or logical App id is ever sent; the server derives them from the session. Anything
// that does not parse exactly is "unavailable": nothing is guessed, no row is invented.
export type NativeDataSelection = {
  artifactId: string;
  versionId: string;
  revisionId: string;
  purpose: 'viewer' | 'editor';
};
export type NativeColumnType =
  | 'text'
  | 'integer'
  | 'decimal'
  | 'boolean'
  | 'date'
  | 'timestamp'
  | 'json'
  | 'file';
export type NativeColumn = {
  id: string;
  label: string;
  type: NativeColumnType;
  nullable: boolean;
  currency?: string;
};
export type NativeCollection = {
  id: string;
  name: string;
  schemaVersion: number;
  columns: NativeColumn[];
};
export type NativeCell =
  | { status: 'present'; value: { type: NativeColumnType; value: unknown } }
  | { status: 'null' }
  | { status: 'missing' };
export type NativeDataset = {
  columns: NativeColumn[];
  rows: { id: string; cells: Record<string, NativeCell> }[];
  hasMore: boolean;
  /** Where the next page of the same read starts; null on the last page. */
  nextCursor: string | null;
  returnedRows: number;
  /** When the rows were READ. Never a row's observed or updated time. */
  fetchedAt: string;
};
export type NativeQueryResult =
  | { ok: true; dataset: NativeDataset }
  | { ok: false; code: string; retryable: boolean };

export class NativeDataUnavailable extends Error {
  constructor(readonly status: number | null) {
    super('native_data_unavailable');
    this.name = 'NativeDataUnavailable';
  }
}

const TYPES = new Set<string>([
  'text',
  'integer',
  'decimal',
  'boolean',
  'date',
  'timestamp',
  'json',
  'file',
]);
const obj = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
const text = (value: unknown) => (typeof value === 'string' && value.length > 0 ? value : null);

function parseColumn(value: unknown): NativeColumn | null {
  const raw = obj(value);
  const id = text(raw?.id);
  const label = typeof raw?.label === 'string' ? raw.label : null;
  if (!raw || !id || label === null || !TYPES.has(String(raw.type))) return null;
  if (typeof raw.nullable !== 'boolean') return null;
  return {
    id,
    label,
    type: raw.type as NativeColumnType,
    nullable: raw.nullable,
    ...(typeof raw.currency === 'string' ? { currency: raw.currency } : {}),
  };
}
function parseColumns(value: unknown): NativeColumn[] | null {
  if (!Array.isArray(value)) return null;
  const columns: NativeColumn[] = [];
  for (const entry of value) {
    const column = parseColumn(entry);
    if (!column) return null;
    columns.push(column);
  }
  return new Set(columns.map((column) => column.id)).size === columns.length ? columns : null;
}

export function parseCollections(value: unknown): NativeCollection[] | null {
  const list = obj(value)?.collections;
  if (!Array.isArray(list)) return null;
  const collections: NativeCollection[] = [];
  for (const entry of list) {
    const raw = obj(entry);
    const id = text(raw?.id);
    const name = text(raw?.name);
    const columns = parseColumns(raw?.columns);
    if (!raw || !id || !name || !columns || typeof raw.schemaVersion !== 'number') return null;
    collections.push({ id, name, schemaVersion: raw.schemaVersion, columns });
  }
  return new Set(collections.map((collection) => collection.id)).size === collections.length
    ? collections
    : null;
}

function parseCell(value: unknown): NativeCell | null {
  const raw = obj(value);
  if (raw?.status === 'null') return { status: 'null' };
  if (raw?.status === 'missing') return { status: 'missing' };
  const present = obj(raw?.value);
  if (raw?.status === 'present' && present && TYPES.has(String(present.type)))
    return {
      status: 'present',
      value: { type: present.type as NativeColumnType, value: present.value },
    };
  return null;
}

export function parseQueryResult(value: unknown): NativeQueryResult | null {
  const raw = obj(value);
  if (raw?.ok === false) {
    const error = obj(raw.error);
    return text(error?.code)
      ? { ok: false, code: String(error!.code), retryable: error!.retryable === true }
      : null;
  }
  const columns = parseColumns(raw?.columns);
  const page = obj(raw?.page);
  const rowsIn = raw?.rows;
  if (
    !raw ||
    raw.schemaVersion !== 'data.v2' ||
    !columns ||
    !page ||
    !Array.isArray(rowsIn) ||
    typeof page.hasMore !== 'boolean' ||
    typeof page.returnedRows !== 'number' ||
    !(page.nextCursor === undefined || page.nextCursor === null || text(page.nextCursor)) ||
    !text(raw.fetchedAt)
  )
    return null;
  const known = new Set(columns.map((column) => column.id));
  const rows: NativeDataset['rows'] = [];
  for (const entry of rowsIn) {
    const row = obj(entry);
    const rowId = text(row?.id);
    const cellsIn = obj(row?.cells);
    if (!row || !rowId || !cellsIn) return null;
    const cells: Record<string, NativeCell> = {};
    for (const [columnId, cell] of Object.entries(cellsIn)) {
      const parsed = parseCell(cell);
      // a cell of a column the dataset does not declare is not displayable: refuse the whole answer
      if (!parsed || !known.has(columnId)) return null;
      cells[columnId] = parsed;
    }
    rows.push({ id: rowId, cells });
  }
  if (new Set(rows.map((row) => row.id)).size !== rows.length) return null;
  return {
    ok: true,
    dataset: {
      columns,
      rows,
      hasMore: page.hasMore,
      nextCursor: text(page.nextCursor),
      returnedRows: page.returnedRows,
      fetchedAt: String(raw.fetchedAt),
    },
  };
}

const path = (artifactId: string) =>
  `/api/v1/company/artifacts/${encodeURIComponent(artifactId)}/data`;

export type NativeDataApi = {
  listCollections(): Promise<NativeCollection[]>;
  query(
    collectionId: string,
    options?: { fields?: string[]; limit?: number; cursor?: string },
  ): Promise<NativeQueryResult>;
};

/** `send` is the frame's SESSION-BOUND fetch (a replaced session ends every call through it). */
export function createNativeDataApi(
  selection: NativeDataSelection,
  send: typeof fetch,
): NativeDataApi {
  const { artifactId, versionId, revisionId, purpose } = selection;
  return {
    async listCollections() {
      const query = new URLSearchParams({ versionId, revisionId, purpose });
      const response = await send(`${path(artifactId)}?${query}`, { credentials: 'include' });
      if (response.status !== 200) throw new NativeDataUnavailable(response.status);
      const collections = parseCollections(await response.json().catch(() => null));
      if (!collections) throw new NativeDataUnavailable(null);
      return collections;
    },
    async query(collectionId, options = {}) {
      const response = await send(path(artifactId), {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          artifactId,
          versionId,
          revisionId,
          purpose,
          collectionId,
          ...(options.fields ? { fields: options.fields } : {}),
          ...(options.limit ? { limit: options.limit } : {}),
          ...(options.cursor ? { cursor: options.cursor } : {}),
        }),
      });
      if (response.status !== 200) throw new NativeDataUnavailable(response.status);
      const result = parseQueryResult(await response.json().catch(() => null));
      if (!result) throw new NativeDataUnavailable(null);
      return result;
    },
  };
}
